import React, { useState, useEffect } from 'react';
import {
  Card,
  List,
  Button,
  Input,
  Space,
  Checkbox,
  Progress,
  Typography,
  Tooltip,
  Popconfirm,
  message,
  Alert,
  Tag,
  Row,
  Col
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined
} from '@ant-design/icons';
import moment from 'moment';
import axios from 'axios';
import { validateActionData, sanitizeTextInput } from '../utils/smartGoalValidation';
import { handleActionError, handleOfflineError, retryApiCall } from '../utils/smartGoalErrorHandling';

const { Text, Title } = Typography;
const { TextArea } = Input;

const ActionManagementComponent = ({
  smartGoal,
  patientGoal = null, // New: specific patient goal for multi-goal structure
  smartGoalId, // Backward compatibility
  actions = [], // Backward compatibility
  completionRate = 0, // Backward compatibility
  onActionsUpdate,
  onUpdate, // New: for updating the entire goal structure
  readOnly = false,
  showProgress = true
}) => {
  // Determine which actions to use (new multi-goal structure or backward compatibility)
  const getActionsData = () => {
    if (patientGoal) {
      return {
        actions: patientGoal.actions || [],
        completionRate: patientGoal.completionRate || 0,
        goalId: smartGoal._id,
        patientGoalId: patientGoal._id
      };
    } else {
      // Backward compatibility: use first patient goal or fallback to old structure
      if (smartGoal && smartGoal.patientGoals && smartGoal.patientGoals.length > 0) {
        const firstGoal = smartGoal.patientGoals[0];
        return {
          actions: firstGoal.actions || [],
          completionRate: firstGoal.completionRate || 0,
          goalId: smartGoal._id,
          patientGoalId: firstGoal._id
        };
      } else {
        return {
          actions: actions,
          completionRate: completionRate,
          goalId: smartGoalId,
          patientGoalId: null
        };
      }
    }
  };

  const actionsData = getActionsData();
  const [localActions, setLocalActions] = useState(actionsData.actions);
  const [newActionText, setNewActionText] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [editingText, setEditingText] = useState('');

  // Update local actions when props change
  useEffect(() => {
    const newActionsData = getActionsData();
    setLocalActions(newActionsData.actions);
  }, [smartGoal, patientGoal, actions]);

  // Calculate completion rate
  const calculateCompletionRate = (actionList) => {
    if (!actionList || actionList.length === 0) return 0;
    const completedCount = actionList.filter(action => action.completed).length;
    return Math.round((completedCount / actionList.length) * 100);
  };

  // Add new action
  const addAction = async () => {
    const trimmedText = newActionText.trim();

    // Validate action data
    const actionData = { description: trimmedText };
    const validation = validateActionData(actionData);

    if (!validation.isValid) {
      message.error(`Action validation failed: ${validation.errors.join(', ')}`);
      return;
    }

    if (!actionsData.goalId) {
      // For creation mode, just update local state
      const newAction = {
        description: sanitizeTextInput(trimmedText),
        completed: false,
        createdAt: new Date()
      };
      const updatedActions = [...localActions, newAction];
      setLocalActions(updatedActions);
      setNewActionText('');

      if (onActionsUpdate) {
        onActionsUpdate(updatedActions);
      }
      return;
    }

    // Check network connectivity
    if (handleOfflineError('add action')) {
      return;
    }

    try {
      setLoading(true);

      let response;
      if (actionsData.patientGoalId) {
        // Use new patient goal endpoint
        response = await retryApiCall(
          () => axios.post(`/smart-goals/${actionsData.goalId}/patient-goals/${actionsData.patientGoalId}/actions`, {
            description: sanitizeTextInput(trimmedText)
          }),
          2,
          500
        );
      } else {
        // Backward compatibility: use old endpoint
        response = await retryApiCall(
          () => axios.post(`/smart-goals/${actionsData.goalId}/actions`, {
            description: sanitizeTextInput(trimmedText)
          }),
          2,
          500
        );
      }

      const updatedGoal = response.data.data.smartGoal;

      // Update local actions based on structure
      if (actionsData.patientGoalId) {
        const updatedPatientGoal = updatedGoal.patientGoals.find(pg => pg._id === actionsData.patientGoalId);
        if (updatedPatientGoal) {
          setLocalActions(updatedPatientGoal.actions);
          if (onActionsUpdate) {
            onActionsUpdate(updatedPatientGoal.actions);
          }
        }
      } else {
        // Backward compatibility
        const actionsToUse = updatedGoal.patientGoals && updatedGoal.patientGoals.length > 0
          ? updatedGoal.patientGoals[0].actions
          : updatedGoal.actions || [];
        setLocalActions(actionsToUse);
        if (onActionsUpdate) {
          onActionsUpdate(actionsToUse);
        }
      }

      setNewActionText('');
      message.success('Action added successfully');

      // Update the entire goal if onUpdate is provided
      if (onUpdate) {
        onUpdate(updatedGoal);
      }
    } catch (error) {
      handleActionError(error, 'add', trimmedText);
    } finally {
      setLoading(false);
    }
  };

  // Toggle action completion (for both local and server mode)
  const toggleActionCompletion = async (index, notes = '') => {
    // Validate index
    if (index < 0 || index >= localActions.length) {
      message.error('Invalid action index');
      return;
    }

    const action = localActions[index];

    // Validate notes if provided and action is being completed
    if (!action.completed && notes && notes.length > 500) {
      message.error('Action notes cannot exceed 500 characters');
      return;
    }

    if (!actionsData.goalId) {
      // For creation mode, just update local state
      const updatedActions = [...localActions];
      const currentAction = updatedActions[index];

      if (currentAction.completed) {
        // Uncomplete action
        currentAction.completed = false;
        currentAction.completedDate = null;
        currentAction.notes = '';
      } else {
        // Complete action
        currentAction.completed = true;
        currentAction.completedDate = new Date();
        if (notes) {
          currentAction.notes = sanitizeTextInput(notes);
        }
      }

      setLocalActions(updatedActions);

      if (onActionsUpdate) {
        onActionsUpdate(updatedActions);
      }
      return;
    }

    // Check network connectivity
    const actionType = action.completed ? 'uncomplete' : 'complete';
    if (handleOfflineError(`${actionType} action`)) {
      return;
    }

    try {
      setLoading(true);

      let response;
      if (actionsData.patientGoalId) {
        // Use new patient goal toggle endpoint
        response = await retryApiCall(
          () => axios.patch(`/smart-goals/${actionsData.goalId}/patient-goals/${actionsData.patientGoalId}/actions/${index}/toggle`, {
            notes: sanitizeTextInput(notes)
          }),
          2,
          500
        );
      } else {
        // Backward compatibility: use old endpoint (now supports toggle)
        response = await retryApiCall(
          () => axios.patch(`/smart-goals/${actionsData.goalId}/actions/${index}/complete`, {
            notes: sanitizeTextInput(notes)
          }),
          2,
          500
        );
      }

      const updatedGoal = response.data.data.smartGoal;

      // Update local actions based on structure
      if (actionsData.patientGoalId) {
        const updatedPatientGoal = updatedGoal.patientGoals.find(pg => pg._id === actionsData.patientGoalId);
        if (updatedPatientGoal) {
          setLocalActions(updatedPatientGoal.actions);
          if (onActionsUpdate) {
            onActionsUpdate(updatedPatientGoal.actions);
          }
        }
      } else {
        // Backward compatibility
        const actionsToUse = updatedGoal.patientGoals && updatedGoal.patientGoals.length > 0
          ? updatedGoal.patientGoals[0].actions
          : updatedGoal.actions || [];
        setLocalActions(actionsToUse);
        if (onActionsUpdate) {
          onActionsUpdate(actionsToUse);
        }
      }

      const successMessage = action.completed ? 'Action uncompleted successfully' : 'Action completed successfully';
      message.success(successMessage);

      // Update the entire goal if onUpdate is provided
      if (onUpdate) {
        onUpdate(updatedGoal);
      }
    } catch (error) {
      handleActionError(error, actionType, action.description);
    } finally {
      setLoading(false);
    }
  };

  // Remove action
  const removeAction = async (index) => {
    if (!actionsData.goalId) {
      // For creation mode, just update local state
      const updatedActions = localActions.filter((_, i) => i !== index);
      setLocalActions(updatedActions);

      if (onActionsUpdate) {
        onActionsUpdate(updatedActions);
      }
      return;
    }

    try {
      setLoading(true);

      let response;
      if (actionsData.patientGoalId) {
        // Use new patient goal endpoint
        response = await axios.delete(`/smart-goals/${actionsData.goalId}/patient-goals/${actionsData.patientGoalId}/actions/${index}`);
      } else {
        // Backward compatibility: use old endpoint
        response = await axios.delete(`/smart-goals/${actionsData.goalId}/actions/${index}`);
      }

      const updatedGoal = response.data.data.smartGoal;

      // Update local actions based on structure
      if (actionsData.patientGoalId) {
        const updatedPatientGoal = updatedGoal.patientGoals.find(pg => pg._id === actionsData.patientGoalId);
        if (updatedPatientGoal) {
          setLocalActions(updatedPatientGoal.actions);
          if (onActionsUpdate) {
            onActionsUpdate(updatedPatientGoal.actions);
          }
        }
      } else {
        // Backward compatibility
        const actionsToUse = updatedGoal.patientGoals && updatedGoal.patientGoals.length > 0
          ? updatedGoal.patientGoals[0].actions
          : updatedGoal.actions || [];
        setLocalActions(actionsToUse);
        if (onActionsUpdate) {
          onActionsUpdate(actionsToUse);
        }
      }

      message.success('Action removed successfully');

      // Update the entire goal if onUpdate is provided
      if (onUpdate) {
        onUpdate(updatedGoal);
      }
    } catch (error) {
      console.error('Error removing action:', error);
      message.error('Failed to remove action');
    } finally {
      setLoading(false);
    }
  };



  // Start editing action
  const startEditing = (index) => {
    setEditingIndex(index);
    setEditingText(localActions[index].description);
  };

  // Save edited action
  const saveEdit = () => {
    if (!editingText.trim()) {
      message.warning('Action description cannot be empty');
      return;
    }

    const updatedActions = [...localActions];
    updatedActions[editingIndex].description = editingText.trim();
    setLocalActions(updatedActions);
    setEditingIndex(-1);
    setEditingText('');

    if (onActionsUpdate) {
      onActionsUpdate(updatedActions);
    }
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingIndex(-1);
    setEditingText('');
  };

  const currentCompletionRate = showProgress ? calculateCompletionRate(localActions) : actionsData.completionRate;

  return (
    <Card
      title={
        <Space>
          <CheckCircleOutlined />
          Action Steps
          {showProgress && (
            <Tag color={currentCompletionRate === 100 ? 'success' : 'processing'}>
              {currentCompletionRate}% Complete
            </Tag>
          )}
        </Space>
      }
      size="small"
      className="action-management-component"
    >
      {/* Progress Bar */}
      {showProgress && localActions.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Row align="middle" gutter={16}>
            <Col flex="auto">
              <Progress 
                percent={currentCompletionRate} 
                status={currentCompletionRate === 100 ? 'success' : 'active'}
                strokeColor={currentCompletionRate === 100 ? '#52c41a' : '#1890ff'}
              />
            </Col>
            <Col>
              <Text type="secondary">
                {localActions.filter(a => a.completed).length} of {localActions.length} completed
              </Text>
            </Col>
          </Row>
        </div>
      )}

      {/* Add New Action */}
      {!readOnly && (
        <div style={{ marginBottom: 16 }}>
          <Space.Compact style={{ width: '100%' }}>
            <Input
              placeholder="Enter a specific action step..."
              value={newActionText}
              onChange={(e) => setNewActionText(e.target.value)}
              onPressEnter={addAction}
              maxLength={200}
              disabled={loading}
            />
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={addAction}
              loading={loading}
            >
              Add Action
            </Button>
          </Space.Compact>
        </div>
      )}

      {/* Actions List */}
      {localActions.length > 0 ? (
        <List
          size="small"
          dataSource={localActions}
          renderItem={(action, index) => (
            <List.Item
              actions={!readOnly ? [
                <Tooltip title="Edit action">
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => startEditing(index)}
                    size="small"
                    disabled={loading || editingIndex === index}
                  />
                </Tooltip>,
                <Popconfirm
                  title="Are you sure you want to remove this action?"
                  onConfirm={() => removeAction(index)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Tooltip title="Remove action">
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      size="small"
                      disabled={loading}
                    />
                  </Tooltip>
                </Popconfirm>
              ] : []}
            >
              <List.Item.Meta
                avatar={
                  <Checkbox
                    checked={action.completed}
                    onChange={() => toggleActionCompletion(index)}
                    disabled={loading || readOnly}
                  />
                }
                title={
                  editingIndex === index ? (
                    <Space.Compact style={{ width: '100%' }}>
                      <Input
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                        onPressEnter={saveEdit}
                        maxLength={200}
                      />
                      <Button 
                        type="primary" 
                        icon={<SaveOutlined />} 
                        onClick={saveEdit}
                        size="small"
                      />
                      <Button 
                        icon={<CloseOutlined />} 
                        onClick={cancelEdit}
                        size="small"
                      />
                    </Space.Compact>
                  ) : (
                    <Text 
                      style={{ 
                        textDecoration: action.completed ? 'line-through' : 'none',
                        color: action.completed ? '#999' : 'inherit'
                      }}
                    >
                      {action.description}
                    </Text>
                  )
                }
                description={
                  action.completed && action.completedDate && (
                    <Space>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Completed {moment(action.completedDate).format('MMM DD, YYYY')}
                      </Text>
                      {action.notes && (
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          • {action.notes}
                        </Text>
                      )}
                    </Space>
                  )
                }
              />
            </List.Item>
          )}
        />
      ) : (
        <Alert
          message="No actions added yet"
          description={readOnly ? "No action steps have been defined for this goal." : "Add specific action steps to achieve this goal."}
          type={readOnly ? "info" : "warning"}
          showIcon
        />
      )}
    </Card>
  );
};

export default ActionManagementComponent;
